import { getRequest } from "@/lib/api";

export type Dog = {
  id: number;
  puppyId: number;
  name: string;
  breed: string;
  breedSlug: string;
  breeder: string;
  breederSlug: string;
  location: string;
  province: string;
  city: string;
  coordinates: { lat: number; lng: number };
  price: string;
  sex: "남아" | "여아";
  age: string;
  image: string;
  tags: string[];
  reservationStatus: "AVAILABLE" | "RESERVED";
};
export type Guide = {
  size: string;
  activity: string;
  grooming: string;
  familyFit: string;
  traits: string[];
  care: { title: string; description: string }[];
  healthQuestions: string[];
  koreanInitial: string;
  body: string;
  weightGuide: string;
  heightGuide: string;
  coat: string;
  group: string;
  recognizedYear: string;
  standardRegistry: string;
  sourceUrl: string;
  seo?: {
    title?: string;
    description?: string;
    keywords?: string[];
    canonicalPath?: string;
  };
};
export type BreedGuide = {
  id: number;
  slug: string;
  name: string;
  englishName: string;
  summary: string;
  image: string;
  guide: Guide;
  listings?: PublicListing[];
};
export type HealthDocument = {
  id: string;
  slug: string;
  title: string;
  category: string;
  issuedAt: string;
  issuer: string;
  result: string;
  reviewedAt: string;
};
export type PublicListing = {
  id: number;
  primaryPuppyId: number;
  name: string;
  sex: "MALE" | "FEMALE";
  birthDate: string;
  price: number | null;
  depositRateBps: number;
  description: string;
  imageUrl: string | null;
  latitude: number | null;
  longitude: number | null;
  publicDetails: Record<string, unknown> | string | null;
  breedName: string;
  breedSlug: string;
  breederName: string;
  breederSlug: string;
  region: string;
  city: string;
  breederImageUrl: string | null;
  breederCoverUrl: string | null;
  breederDetails: Record<string, unknown> | string | null;
  reservationStatus: "AVAILABLE" | "RESERVED";
  parents?: {
    sire: { name: string; birthDate?: string; registrationNumber?: string | null } | null;
    dam: { name: string; birthDate?: string; registrationNumber?: string | null } | null;
  };
  healthDocuments?: HealthDocument[];
  availablePuppies?: PublicListing[];
  breederProfile?: Omit<PublicBreeder, "coverUrl" | "evidenceBadges" | "listings">;
  recommendations?: { sameBreed: PublicListing[]; sameRegion: PublicListing[] };
};
export type EvidenceBadge = {
  type:
    | "IDENTITY"
    | "BREEDING_ENVIRONMENT"
    | "PEDIGREE"
    | "HEALTH_TEST"
    | "LEGAL_COMPLIANCE";
  basis: string;
  checkedAt: string;
  expiresAt: string | null;
  checkedBy: string;
};
export type PublicBreeder = {
  id: string;
  slug: string;
  name: string;
  introduction: string;
  breedingPhilosophy: string;
  environmentPolicy: string;
  socializationPolicy: string;
  returnPolicy: string;
  yearsOfExperience: number;
  region: string;
  city: string;
  imageUrl: string | null;
  coverUrl: string | null;
  publicDetails: Record<string, unknown> | null;
  evidenceBadges: EvidenceBadge[];
  listings: PublicListing[];
};

const fallbackImage = "/images/ai/golden-retriever.png";
const breedImages: Record<string, string> = {
  "golden-retriever": "/images/ai/golden-retriever.png",
  "bichon-frise": "/images/ai/bichon-frise.png",
  "welsh-corgi": "/images/ai/welsh-corgi.png",
  poodle: "/images/ai/poodle.png",
  pomeranian: "/images/ai/pomeranian.png",
  jindo: "/images/ai/jindo.png",
};
const localBreedImage = (slug: string) => breedImages[slug] ?? fallbackImage;
const localImage = (url: string | null | undefined, fallback: string) =>
  url?.startsWith("/") ? url : fallback;
const normalizeListing = (listing: PublicListing): PublicListing => ({
  ...listing,
  imageUrl: localImage(listing.imageUrl, localBreedImage(listing.breedSlug)),
  breederImageUrl: localImage(listing.breederImageUrl, "/images/ai/breeder-profile.png"),
  breederCoverUrl: localImage(listing.breederCoverUrl, "/images/ai/breeder-cover.png"),
  breederProfile: listing.breederProfile
    ? {
        ...listing.breederProfile,
        imageUrl: localImage(listing.breederProfile.imageUrl, "/images/ai/breeder-profile.png"),
      }
    : undefined,
});
const parseObject = (value: Record<string, unknown> | string | null) => {
  if (!value) return {};
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value) as Record<string, unknown>;
  } catch {
    return {};
  }
};
const strings = (value: unknown) =>
  Array.isArray(value) ? value.map(String).map((item) => item.trim()).filter(Boolean) : [];
const text = (value: unknown, fallback: string) =>
  typeof value === "string" && value.trim() ? value.trim() : fallback;
const normalizeGuide = (value: unknown): Guide => {
  const raw = parseObject((value ?? null) as Record<string, unknown> | string | null);
  const traits = strings(raw.traits).length ? strings(raw.traits) : strings(raw.temperament);
  const rawCare = Array.isArray(raw.care) ? raw.care : [];
  const care = rawCare
    .map((item) => parseObject(item as Record<string, unknown> | string | null))
    .map((item) => ({ title: text(item.title, "돌봄"), description: text(item.description, "개체에 맞는 돌봄 계획을 확인하세요.") }));
  const seo = parseObject((raw.seo ?? null) as Record<string, unknown> | string | null);
  return {
    size: text(raw.size, "개체별 차이 확인"),
    activity: text(raw.activity, "규칙적인 활동 필요"),
    grooming: text(raw.grooming, "피모에 맞는 정기 관리"),
    familyFit: text(raw.familyFit, "가족의 생활 환경과 적합성 확인"),
    traits: traits.length ? traits : ["개체별 성향 확인", "긍정적 사회화 권장"],
    care: care.length
      ? care
      : [{ title: "생활 관리", description: "활동량과 피모, 건강 상태에 맞는 돌봄 계획을 세워주세요." }],
    healthQuestions: strings(raw.healthQuestions).length
      ? strings(raw.healthQuestions)
      : ["부모견 건강검사 결과와 정기 검진 계획을 확인했나요?"],
    koreanInitial: text(raw.koreanInitial, "#"),
    body: text(raw.body, "체형 정보는 AKC 원문과 부모견 자료를 함께 확인하세요."),
    weightGuide: text(raw.weightGuide, "AKC 품종별 체중 자료 확인"),
    heightGuide: text(raw.heightGuide, text(raw.size, "AKC 품종표준 확인")),
    coat: text(raw.coat, "개체의 실제 피모 상태를 확인하세요."),
    group: text(raw.group, "AKC"),
    recognizedYear: raw.recognizedYear === undefined || raw.recognizedYear === null ? "확인 필요" : String(raw.recognizedYear),
    standardRegistry: text(raw.standardRegistry, "AKC"),
    sourceUrl: text(raw.sourceUrl, ""),
    seo: {
      title: text(seo.title, ""),
      description: text(seo.description, ""),
      keywords: strings(seo.keywords),
      canonicalPath: text(seo.canonicalPath, ""),
    },
  };
};
const ageLabel = (birthDate: string) =>
  `생후 ${Math.max(0, Math.floor((Date.now() - new Date(birthDate).getTime()) / (7 * 86400000)))}주`;
export function mapListing(listing: PublicListing): Dog {
  listing = normalizeListing(listing);
  const details = parseObject(listing.publicDetails);
  return {
    id: Number(listing.id),
    puppyId: Number(listing.primaryPuppyId),
    name: listing.name,
    breed: listing.breedName,
    breedSlug: listing.breedSlug,
    breeder: listing.breederName,
    breederSlug: listing.breederSlug,
    location: `${listing.region} ${listing.city}`,
    province: listing.region,
    city: listing.city,
    coordinates: {
      lat: Number(listing.latitude ?? 0),
      lng: Number(listing.longitude ?? 0),
    },
    price:
      Number(listing.price) === 0
        ? "무료분양"
        : listing.price === null
        ? "상담 후 결정"
        : `${Math.round(Number(listing.price) / 10000)}만원`,
    sex: listing.sex === "MALE" ? "남아" : "여아",
    age: ageLabel(listing.birthDate),
    image: listing.imageUrl ?? localBreedImage(listing.breedSlug),
    tags: Array.isArray(details.tags) ? details.tags.map(String) : [],
    reservationStatus: listing.reservationStatus ?? "AVAILABLE",
  };
}
export const getPublicDogs = async () =>
  (await getRequest<PublicListing[]>("listings")).map(mapListing);
const breedQuery = (query?: string, initial?: string) => {
  const params = new URLSearchParams();
  if (query?.trim()) params.set("q", query.trim());
  if (initial?.trim()) params.set("initial", initial.trim());
  return params.size ? `breeds?${params.toString()}` : "breeds";
};
export const getBreedGuides = async (query?: string, initial?: string) =>
  (
    await getRequest<
      Array<{
        id: number;
        slug: string;
        name: string;
        englishName: string;
        summary: string;
        imageUrl: string;
        guide: Guide;
      }>
    >(breedQuery(query, initial))
  ).map((b) => ({ ...b, guide: normalizeGuide(b.guide), image: localImage(b.imageUrl, localBreedImage(b.slug)) }));

const serverBase = () =>
  process.env.API_URL_BACK ?? "http://localhost:8000/";
async function serverGet<T>(path: string): Promise<T> {
  const referenceData = path === "breeds";
  const response = await Promise.race([
    fetch(`${serverBase()}${path}`, {
      next: {
        revalidate: referenceData ? 43_200 : 600,
        tags: referenceData ? [`catalog:${path}`] : [`catalog:${path}`, "catalog:listings"],
      },
      signal: AbortSignal.timeout(10_000),
    }),
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error(`catalog timeout: ${path}`)), 10_000),
    ),
  ]);
  if (!response.ok) throw new Error(`catalog ${response.status}`);
  return response.json() as Promise<T>;
}
export const getServerDogs = async () =>
  (await serverGet<PublicListing[]>("listings")).map(mapListing);
export const getServerListing = async (id: number) =>
  normalizeListing(await serverGet<PublicListing>(`listings/${id}`));
export const getServerBreeds = async (query?: string, initial?: string) =>
  (
    await serverGet<
      Array<{
        id: number;
        slug: string;
        name: string;
        englishName: string;
        summary: string;
        imageUrl: string;
        guide: Guide;
      }>
    >(breedQuery(query, initial))
  ).map((b) => ({ ...b, guide: normalizeGuide(b.guide), image: localImage(b.imageUrl, localBreedImage(b.slug)) }));
export const getServerBreed = async (slug: string) => {
  const breed = await serverGet<{
    id: number;
    slug: string;
    name: string;
    englishName: string;
    summary: string;
    imageUrl: string;
    guide: Guide;
    listings: PublicListing[];
  }>(`breeds/${slug}`);
  return { ...breed, guide: normalizeGuide(breed.guide), imageUrl: localImage(breed.imageUrl, localBreedImage(breed.slug)) };
};
export const getServerBreeder = async (slug: string) => {
  const breeder = await serverGet<PublicBreeder>(`public-breeders/${slug}`);
  return {
    ...breeder,
    imageUrl: localImage(breeder.imageUrl, "/images/ai/breeder-profile.png"),
    coverUrl: localImage(breeder.coverUrl, "/images/ai/breeder-cover.png"),
    listings: breeder.listings.map(normalizeListing),
  };
};
export const getServerHealthDocument = (id: number, slug: string) =>
  serverGet<HealthDocument>(`listings/${id}/health/${slug}`);
